// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ChartHasThresholdOptionBuilder" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import DefaultChartOptionsBuilder from './Izenda.DefaultChartOptionsBuilder';
import {
  each,
  size,
  map,
  getOr,
  nth,
  isNil,
  uniq,
  find,
  flow,
  get,
  assignIn
} from 'lodash/fp';
import {
  ContainerName,
  THRESHOLD_TYPES
} from 'logic/data/Izenda.ReportPartChartConstants';
import { exportClass } from 'share/Izenda.ServiceProvider';

@exportClass('ChartHasThresholdOptionBuilder')
export default class ChartHasThresholdOptionBuilder extends DefaultChartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }
  /**
   * Build chart which has threshold
   * @param {string} visualType Highchart chart type
   * @param {Object} userOptions User's options selected
   * @param {Object} chartData Chart data
   * @param {boolean} supportMetricAll for charts which support metric ALL or Not
   */
  buildOptionsByType(
    visualType: string,
    userOptions: any,
    dataParser: any,
    supportMetricAll: boolean = true
  ): any {
    const { LABELS, VALUES, DYNAMIC_THRESHOLD } = ContainerName;
    const dataStructure = dataParser.dataStructure || {};
    const yAxisContainerName = getOr(
      VALUES,
      'Y_AXIS_CONTAINER_NAME',
      userOptions
    );
    const xAxisContainerName = getOr(
      LABELS,
      'X_AXIS_CONTAINER_NAME',
      userOptions
    );

    const allDynamicThresholdFields = dataStructure[DYNAMIC_THRESHOLD];
    const yAxisContainerFields = dataStructure[yAxisContainerName];
    const xAxisField = nth(
      dataParser.expandedLevel,
      dataStructure[xAxisContainerName]
    );

    const xAxisInfo = dataParser.getDataByField(xAxisField);
    let chartOptions = super.buildOptionsByType(
      visualType,
      userOptions,
      dataParser
    );
    const metricAllDynamicThresholdOption = getOr(
      [],
      'commonXYAxis.metricAllDynamicThresholdOption',
      userOptions
    );
    const staticThresholdMetricAll = getOr(
      [],
      'commonXYAxis.metricAllPlotLinesOption',
      userOptions
    );

    const { dynamicThresholdSettings, thresholdFields } = this.findThresholdFields(
      allDynamicThresholdFields,
      yAxisContainerFields,
      metricAllDynamicThresholdOption,
      supportMetricAll
    );

    let thresholdSeries =
      !isNil(xAxisField) && !isNil(thresholdFields)
        ? this.getSeries(
            dataParser,
            userOptions,
            xAxisField,
            uniq(xAxisInfo.data),
            thresholdFields
          )
        : [];

    thresholdSeries = this.buildStyleForSeries(
      thresholdSeries,
      dynamicThresholdSettings,
      userOptions
    );

    if (supportMetricAll) {
      // static threshold for all
      const plotLines = getOr([], 'yAxis.plotLines', chartOptions);
      plotLines.push(...staticThresholdMetricAll);

      chartOptions = assignIn(chartOptions, {
        plotLines: plotLines
      });
    }

    return assignIn(chartOptions, {
      thresholdSeries: thresholdSeries
    });
  }


   /**
   * Find the threshold fields
   * @param {Array} allThresholdFields array of threshold field in the Custom Container
   * @param {Array} yFields array of field on the Y container
   * @param {Object} metricAllDynamicThresholdOption that contain dynamic setting for option all
   * @param {boolean} supportMetricAll for charts which support metric ALL or Not
   * @return {object} return object which container threshold and their setting
   */
  findThresholdFields(
    allThresholdFields: any,
    yFields: any,
    metricAllDynamicThresholdOption: any,
    supportMetricAll: boolean
  ): any {
    const fieldOptions = this.fieldOptions;
    let thresholdFields = [];
    let dynamicThresholdSettings = [];

    const addThresholdField = fieldName => {
      const thresholdField = find(
        { fieldNameAlias: fieldName },
        allThresholdFields
      );
      if (!isNil(thresholdField)) {
        thresholdFields.push(thresholdField);
      }
    };

    // go through all fields of Y container to get the list of threshold field belong to
    each(field => {
      const fieldNameAlias = field.fieldNameAlias;
      const dynamicThreshold = flow(
        get(fieldNameAlias),
        getOr([], 'dynamicThresholds')
      )(fieldOptions);

      if (size(dynamicThreshold) > 0) {
        dynamicThresholdSettings.push(...dynamicThreshold);
        each(line => {
          addThresholdField(line.fieldName);
        }, dynamicThreshold);
      }
    }, yFields);

    // go through all threshold setting of metric ALL and combine with threshold field of Y field
    if (supportMetricAll) {
      each(setting => {
        dynamicThresholdSettings.push(setting);
        addThresholdField(setting.fieldName);
      }, metricAllDynamicThresholdOption);
    }
    return { dynamicThresholdSettings, thresholdFields };
  }

   /**
   * Build series for threshold
   * @param {Array} thresholdSeries list of serie
   * @param {Array} dynamicThresholdSettings setting of threshold
   * @param {Object} userOptions User's options selected
   * @return {Array} threshold series were apply setting
   */
  buildStyleForSeries(
    thresholdSeries: any,
    dynamicThresholdSettings: any,
    userOptions: any
  ): any {
    const isSpline = getOr(false, 'izendaOptions.spline', userOptions);

    thresholdSeries = map(serie => {
      const setting = find(
        { fieldName: serie.yAxisField.fieldNameAlias },
        dynamicThresholdSettings
      );
      const text = get('label.text', setting);
      serie = assignIn(
        serie,
        {
          label: {
            enabled: true
          },
          marker: {
            enabled: false
          },
          type: isSpline ? THRESHOLD_TYPES.SPLINE : THRESHOLD_TYPES.LINE,
          color: setting.color,
          colorByPoint: false,
          lineWidth: setting.width,
          dashStyle: setting.dashStyle,
          isThreshold: true
        }
      );

      serie.name = text;
      return serie;
    }, thresholdSeries);
    return thresholdSeries;
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.ChartHasThresholdOptionBuilder.js